/**
 * チャンギ盤 (9 列 × 10 段、 交点配置) コンポーネント (MVP)。
 *
 * - 駒は交点に置く (チェス/将棋と違い、 マス目中央ではない)
 * - 九宮 (3×3 角形) と九宮内対角線 (X 字) を装飾線で描画
 * - Cho (青/楚) 先手、 Han (赤/漢) 後手。 駒は円盤 + ハングル/漢字
 * - 駒タップ → 合法 to をハイライト → タップで着手
 * - パスは「한수쉼」 ボタン。 王手中は disabled
 * - 점수表示: Cho NN (内訳)   Han NN+1.5 (内訳)
 * - 빅장 警告: 自分が빅장を成立させたか、 相手が成立させた直後にバナー表示
 *
 * 反転表示: yourSide='Second' (Han) のとき盤を 180 度反転 (= 上下と左右の反転)。
 */

import { useState } from 'react';
import {
  isJanggiMove,
  isJanggiPass,
  JANGGI_FILES,
  JANGGI_RANKS,
  type JanggiMove,
  type JanggiPieceKind,
  type JanggiState,
  janggiLegalTargetsFor,
  janggiPieceCounts,
  janggiScores,
  type Side,
} from '../../games/janggi';
import { useFreshLastMove } from '../../lib/first-paint';

interface Props {
  state: JanggiState;
  legal?: JanggiMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: JanggiMove) => void;
  mini?: boolean;
  lastMove?: JanggiMove;
  staticView?: boolean;
  /** チュートリアル: 強調する交点 [file, rank] の配列。 */
  tutorialHighlight?: [number, number][];
}

/** 駒の表示文字 (漢字: 楚 Cho / 漢 Han の伝統表記。 ハングル併用も可)。 */
const PIECE_GLYPH_CHO: Record<JanggiPieceKind, string> = {
  King: '楚',
  Advisor: '士',
  Chariot: '車',
  Cannon: '包',
  Horse: '馬',
  Elephant: '象',
  Soldier: '卒',
};
const PIECE_GLYPH_HAN: Record<JanggiPieceKind, string> = {
  King: '漢',
  Advisor: '士',
  Chariot: '車',
  Cannon: '包',
  Horse: '馬',
  Elephant: '象',
  Soldier: '兵',
};

/** 駒のサイズクラス: 큰駒 (車包) / 중駒 (馬象) / 작은駒 (士卒) / 王。 */
function pieceSizeClass(kind: JanggiPieceKind): string {
  switch (kind) {
    case 'Chariot':
    case 'Cannon':
      return 'janggi-piece-large';
    case 'Horse':
    case 'Elephant':
      return 'janggi-piece-medium';
    case 'King':
      return 'janggi-piece-king';
    default:
      return 'janggi-piece-small';
  }
}

function pieceGlyph(kind: JanggiPieceKind, owner: Side): string {
  return owner === 'First' ? PIECE_GLYPH_CHO[kind] : PIECE_GLYPH_HAN[kind];
}

export function JanggiBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
}: Props) {
  // 盤を開いた瞬間は直前手のリングを出さない (mount 後の新しい手だけアニメする)。
  const freshLast = useFreshLastMove(lastMove);
  const isMyTurn =
    myTurnAllowed !== undefined ? myTurnAllowed : yourSide != null && yourSide === state.side;
  const showOppHint = !mini && !staticView && !isMyTurn;
  const [selected, setSelected] = useState<[number, number] | null>(null);

  const targets = selected
    ? janggiLegalTargetsFor(legal ?? [], selected)
    : [];

  // 反転表示: Han 視点なら 180 度反転。 First/未設定なら Cho 視点。
  const flip = yourSide === 'Second';
  // rank の描画順 (上→下): Cho 視点では rank=9..0、 Han 視点では rank=0..9
  const ranks: number[] = flip
    ? Array.from({ length: JANGGI_RANKS }, (_, i) => i)
    : Array.from({ length: JANGGI_RANKS }, (_, i) => JANGGI_RANKS - 1 - i);
  const files: number[] = flip
    ? Array.from({ length: JANGGI_FILES }, (_, i) => JANGGI_FILES - 1 - i)
    : Array.from({ length: JANGGI_FILES }, (_, i) => i);

  // (file, rank) クリック
  const handleIntersectClick = (f: number, r: number) => {
    if (mini || !isMyTurn) return;
    const cell = state.board[r][f];
    if (!selected) {
      if (cell && cell.owner === state.side) {
        setSelected([f, r]);
      }
      return;
    }
    // 再選択 (自駒)
    if (cell && cell.owner === state.side) {
      setSelected([f, r]);
      return;
    }
    // 着手 (target?)
    const cand = targets.find(
      (m) => m.Move.to[0] === f && m.Move.to[1] === r,
    );
    if (cand) {
      onMove?.(cand);
      setSelected(null);
      return;
    }
    // それ以外は選択解除
    setSelected(null);
  };

  // パス合法か
  const canPass =
    (legal ?? []).some((m) => isJanggiPass(m)) && isMyTurn && !mini;

  const lastFrom: [number, number] | null = lastMove && isJanggiMove(lastMove) ? lastMove.Move.from : null;
  const lastTo: [number, number] | null = lastMove && isJanggiMove(lastMove) ? lastMove.Move.to : null;

  const { cho, han } = janggiScores(state);
  const choPieces = janggiPieceCounts(state, 'First');
  const hanPieces = janggiPieceCounts(state, 'Second');
  const hanWithDeom = han + state.deom_han_x10 / 10;

  // 빅장 警告: state.bikjang_pending が Some(side) のとき、 次手番側 (= state.side) に
  // 「상대가 빅장을 仕掛けた」 と表示。 自分が仕掛けた直後にも表示。
  const bikjangSide = state.bikjang_pending;

  return (
    <div className={`janggi-frame ${mini ? 'mini' : ''} ${showOppHint ? 'not-my-turn' : ''}`}>
      {!mini && (
        <div className="janggi-scorebar" aria-label="janggi-score">
          <span className={`janggi-score-cho ${cho > hanWithDeom ? 'leading' : ''}`}>
            <span className="janggi-score-label">Cho</span>
            <span className="janggi-score-num">{cho}</span>
            <span className="janggi-score-pieces">
              車{choPieces.Chariot} 包{choPieces.Cannon} 馬{choPieces.Horse} 象{choPieces.Elephant} 士{choPieces.Advisor} 卒{choPieces.Soldier}
            </span>
          </span>
          <span className={`janggi-score-han ${hanWithDeom > cho ? 'leading' : ''}`}>
            <span className="janggi-score-label">Han</span>
            <span className="janggi-score-num">{han}+1.5</span>
            <span className="janggi-score-pieces">
              車{hanPieces.Chariot} 包{hanPieces.Cannon} 馬{hanPieces.Horse} 象{hanPieces.Elephant} 士{hanPieces.Advisor} 兵{hanPieces.Soldier}
            </span>
          </span>
        </div>
      )}
      {!mini && bikjangSide && (
        <div className="janggi-bikjang-warn" data-testid="janggi-bikjang">
          빅장 (両王対峙) — {bikjangSide === 'First' ? 'Cho' : 'Han'} が仕掛けています。
          次手で解除しなければ점수勝負に移行します。
        </div>
      )}
      <div className="janggi-board" data-testid="janggi-board">
        {/* 罫線 / 九宮 / 河 を SVG で描画 (= セル中央交差)。 Xiangqi 方式で「線がセル中央
            を通る」 ように描き、 駒は flex center でセル中央に置けば自動的に「交点」 配置になる。
            viewBox は 9*100 × 10*100 = 900×1000、 線は (f*100+50, r*100+50) 起点。
            preserveAspectRatio="none" で板アスペクトに合わせて伸縮させる。 */}
        <svg
          className="janggi-bg"
          viewBox={`0 0 ${JANGGI_FILES * 100} ${JANGGI_RANKS * 100}`}
          preserveAspectRatio="none"
          aria-hidden
        >
          {Array.from({ length: JANGGI_FILES }, (_, f) => (
            <line
              key={`v-${f}`}
              x1={f * 100 + 50}
              y1={50}
              x2={f * 100 + 50}
              y2={JANGGI_RANKS * 100 - 50}
              stroke="currentColor"
              strokeWidth={2}
              opacity={0.55}
            />
          ))}
          {Array.from({ length: JANGGI_RANKS }, (_, r) => (
            <line
              key={`h-${r}`}
              x1={50}
              y1={r * 100 + 50}
              x2={JANGGI_FILES * 100 - 50}
              y2={r * 100 + 50}
              stroke="currentColor"
              strokeWidth={2}
              opacity={0.55}
            />
          ))}
          {/* 九宮の対角線: 上 (Han) と下 (Cho) の 3×3 領域に X 字。 file 3..5 × rank 0..2 / 7..9 */}
          <line x1={3 * 100 + 50} y1={50} x2={5 * 100 + 50} y2={2 * 100 + 50} stroke="currentColor" strokeWidth={2} opacity={0.55} />
          <line x1={5 * 100 + 50} y1={50} x2={3 * 100 + 50} y2={2 * 100 + 50} stroke="currentColor" strokeWidth={2} opacity={0.55} />
          <line x1={3 * 100 + 50} y1={7 * 100 + 50} x2={5 * 100 + 50} y2={9 * 100 + 50} stroke="currentColor" strokeWidth={2} opacity={0.55} />
          <line x1={5 * 100 + 50} y1={7 * 100 + 50} x2={3 * 100 + 50} y2={9 * 100 + 50} stroke="currentColor" strokeWidth={2} opacity={0.55} />
        </svg>
        {/* 9 × 10 のグリッドセル: 各セル中央が罫線交差 (= 交点) と一致する。 駒はセル中央配置。 */}
        {ranks.flatMap((r) =>
          files.map((f) => {
            const cell = state.board[r][f];
            const isSelected = selected != null && selected[0] === f && selected[1] === r;
            const isTarget = targets.some(
              (m) => m.Move.to[0] === f && m.Move.to[1] === r,
            );
            const isLast =
              !mini &&
              ((lastFrom != null && lastFrom[0] === f && lastFrom[1] === r) ||
                (lastTo != null && lastTo[0] === f && lastTo[1] === r));
            const isTutorialHint =
              tutorialHighlight?.some(([hf, hr]) => hf === f && hr === r) ?? false;
            // 九宮内のセル (= 装飾用クラス)
            const inPalaceCho = f >= 3 && f <= 5 && r >= 0 && r <= 2;
            const inPalaceHan = f >= 3 && f <= 5 && r >= 7 && r <= 9;
            return (
              <button
                key={`${f}-${r}`}
                className={`janggi-intersection ${isSelected ? 'selected' : ''} ${
                  isTarget ? 'target' : ''
                } ${isLast ? 'last-move' : ''} ${isLast && !freshLast ? 'no-anim' : ''} ${isTutorialHint ? 'tutorial-hint' : ''} ${
                  inPalaceCho ? 'palace-cho' : ''
                } ${inPalaceHan ? 'palace-han' : ''}`}
                style={{
                  // intersection の位置: 0-indexed の files/ranks 位置 (画面座標 = flip 後)。
                  // CSS grid だと f, r 反転を直接表現しづらいので絶対位置を style で。
                  gridColumn: (flip ? JANGGI_FILES - f : f + 1).toString(),
                  gridRow: (flip ? r + 1 : JANGGI_RANKS - r).toString(),
                }}
                disabled={mini}
                onClick={() => handleIntersectClick(f, r)}
                aria-label={`${String.fromCharCode('a'.charCodeAt(0) + f)}${r + 1}`}
                data-file={f}
                data-rank={r}
              >
                {cell && (
                  <span
                    className={`janggi-piece ${
                      cell.owner === 'First' ? 'cho' : 'han'
                    } ${pieceSizeClass(cell.kind)}`}
                  >
                    {pieceGlyph(cell.kind, cell.owner)}
                  </span>
                )}
              </button>
            );
          }),
        )}
      </div>
      {canPass && (
        <div className="janggi-actions">
          <button
            type="button"
            className="janggi-pass-btn"
            data-testid="janggi-pass"
            onClick={() => onMove?.('Pass')}
          >
            한수쉼 (パス)
          </button>
        </div>
      )}
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}
